import { type FC, type ReactNode, useCallback, useMemo } from 'react';
import { Divider, Form, Input, Tooltip, Button } from 'antd';
import type { FormInstance } from 'antd';
import { InfoCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';

import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import type { PromoFieldConfig, RuleOption } from '../../config/tpoGenConfig';
import type { ParsedPromoOptions } from '../../config/parsePromoConfig';
import { generatePromoName } from './utils/generatePromoName';
import renderPromoField from './components/renderPromoField';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { useRuleBoundForm } from '../../rules/store/useRuleBoundForm';
import { buildOptionResolvers } from '../../rules/resolvers/optionResolvers';
import type { ResolvedDateConstraints } from '../../rules/types';
import { TPO_PROMO_FORM, tpoFieldPartTestId, tpoFieldTestId } from '../../config/testIds';
import { useEditSession } from '../EditSession/EditSessionContext';

const ITEMS_PER_ROW = 5;

const EMPTY_PARSED_OPTIONS: ParsedPromoOptions = { promoTypeOptions: [] };

type Option = { label: string; value: string; disabled?: boolean };

interface PromoMetaFormProps {
    form: FormInstance;
    /** Per-promo-tab id; isolates this form's rule-engine working set. */
    instanceId: string;
    promotionId?: string | null;
    parsedOptions?: ParsedPromoOptions;
    onFormValuesChange?: (changedValues: Record<string, unknown>) => void;
    isUpliftPending?: boolean;
}

const renderLabel = (field: PromoFieldConfig, onAutoPopulate?: () => void): ReactNode => {
    const hasTooltip = !!field.tooltip;
    const hasAutoPopulate = !!field.autoPopulate?.enabled;

    if (!hasTooltip && !hasAutoPopulate) return field.label;

    return (
        <span className="inline-flex items-center gap-1">
            {field.label}
            {hasTooltip && (
                <Tooltip title={field.tooltip}>
                    <InfoCircleOutlined className="text-gray-400 text-xs" />
                </Tooltip>
            )}
            {hasAutoPopulate && (
                <Tooltip title={field.autoPopulate!.tooltip}>
                    <Button
                        type="link"
                        size="small"
                        icon={<ThunderboltOutlined />}
                        className="!p-0 !h-auto"
                        onClick={onAutoPopulate}
                        data-testid={tpoFieldPartTestId('promo', field.name, 'auto_populate_btn')}
                    />
                </Tooltip>
            )}
        </span>
    );
};

const chunkArray = <T,>(arr: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
        chunks.push(arr.slice(i, i + size));
    }
    return chunks;
};

const PromoMetaForm: FC<PromoMetaFormProps> = ({
    form: promoForm,
    instanceId,
    parsedOptions = EMPTY_PARSED_OPTIONS,
    onFormValuesChange,
    isUpliftPending = false,
}) => {
    const { promoFields } = useTpoGenConfig();
    const { currentPlanDetails } = usePlanDetailsContext();
    const { canEdit } = useEditSession();
    const watchedValues = Form.useWatch([], promoForm);
    const promoFormValues: Record<string, any> = useMemo(() => watchedValues ?? {}, [watchedValues]);
    const planYear = currentPlanDetails?.plan_year;

    // Rule-engine bridge. Supplies dynamic promo options + each field's base
    // options to the engine resolvers.
    const baseOptionsByTarget = useMemo(() => {
        const map: Record<string, RuleOption[]> = {};
        for (const f of promoFields) {
            if (f.options) map[`promo:${f.name}`] = f.options as RuleOption[];
            // Register embedded unit-toggle fields so `disableByMap` rules targeting
            // them have a base option list to resolve against (engine authority).
            if (f.unitToggle) map[`promo:${f.unitToggle.unitField}`] = f.unitToggle.options as RuleOption[];
        }
        return map;
    }, [promoFields]);
    const resolvers = useMemo(
        () => buildOptionResolvers({ parsedPromoOptions: parsedOptions, baseOptionsByTarget }),
        [parsedOptions, baseOptionsByTarget],
    );
    const { derivedStates, onValuesChange: ruleOnValuesChange } = useRuleBoundForm('promo', promoForm, {
        resolvers,
        instanceId,
    });

    const unitFields = useMemo<{ name: string; defaultValue: string }[]>(
        () =>
            promoFields
                .filter((f) => f.unitToggle)
                .map((f) => ({
                    name: f.unitToggle!.unitField,
                    defaultValue: f.unitToggle!.defaultValue ?? f.unitToggle!.options[0]?.value,
                }))
                .filter((entry, idx, arr) => arr.findIndex((e) => e.name === entry.name) === idx),
        [promoFields],
    );

    // Config-declared hidden constant fields (e.g. the fixed offer method). Rendered
    // as form-only inputs so their value flows into the payload without a layout row.
    const constantFields = useMemo<{ name: string; value: string }[]>(
        () =>
            promoFields
                .filter((f) => f.hidden && f.constantValue !== undefined)
                .map((f) => ({ name: f.name, value: f.constantValue as string })),
        [promoFields],
    );

    // Per-field UI state derived from the rule engine.
    const fieldStates = useMemo(() => {
        const map: Record<
            string,
            {
                visible: boolean;
                disabled: boolean;
                options: Option[];
                required: boolean;
                dateConstraints?: ResolvedDateConstraints;
            }
        > = {};
        for (const field of promoFields) {
            const derived = derivedStates[field.name];
            map[field.name] = {
                visible: derived?.visible ?? true,
                // Read-only wins over any rule decision.
                disabled: !canEdit || (derived?.disabled ?? false),
                options: (derived?.options as Option[] | undefined) ?? field.options ?? [],
                required: derived?.required ?? false,
                dateConstraints: derived?.dateConstraints,
            };
        }
        return map;
    }, [promoFields, derivedStates, canEdit]);

    const visibleFields = useMemo(
        () => promoFields.filter((field) => !field.hidden && fieldStates[field.name]?.visible),
        [promoFields, fieldStates],
    );

    const rows = useMemo(() => chunkArray(visibleFields, ITEMS_PER_ROW), [visibleFields]);

    const handleAutoPopulate = useCallback(() => {
        const currentValues = promoForm.getFieldsValue();
        const name = generatePromoName(currentValues, parsedOptions);
        if (name) {
            promoForm.setFieldsValue({ promoName: name });
        }
    }, [promoForm, parsedOptions]);

    const handlePromoFormValuesChange = useCallback(
        (changedValues: Record<string, any>) => {
            // The store runs reset/autoFill reactions and routes them back to the form.
            ruleOnValuesChange(changedValues);
            onFormValuesChange?.(changedValues);
        },
        [ruleOnValuesChange, onFormValuesChange],
    );

    return (
        <Form
            form={promoForm}
            name="promo-meta-form-v2"
            layout="vertical"
            onValuesChange={handlePromoFormValuesChange}
            className={isUpliftPending ? 'pointer-events-none opacity-60' : ''}
            data-testid={TPO_PROMO_FORM}
        >
            {unitFields.map((uf) => (
                <Form.Item key={uf.name} name={uf.name} initialValue={uf.defaultValue} hidden noStyle>
                    <Input type="hidden" />
                </Form.Item>
            ))}
            {constantFields.map((cf) => (
                <Form.Item key={cf.name} name={cf.name} initialValue={cf.value} hidden noStyle>
                    <Input type="hidden" />
                </Form.Item>
            ))}
            {rows.map((row, rowIdx) => (
                <div key={rowIdx}>
                    {rowIdx > 0 && <Divider className="!my-0" />}
                    <div className={`flex flex-row gap-x-6 ${rowIdx > 0 ? 'mt-4' : ''}`}>
                        {row.map((field) => {
                            const state = fieldStates[field.name];
                            const options = state.options;
                            const disabled = state.disabled;
                            // Engine-derived (disabled-aware) options for the embedded
                            // unit toggle, falling back to the static config list.
                            const unitOptions = field.unitToggle
                                ? ((derivedStates[field.unitToggle.unitField]?.options as Option[] | undefined) ??
                                  (field.unitToggle.options as Option[]))
                                : undefined;

                            return (
                                <Form.Item
                                    key={field.name}
                                    label={renderLabel(field, handleAutoPopulate)}
                                    name={field.name}
                                    className="flex-1"
                                    rules={
                                        state.required && !disabled
                                            ? [
                                                  {
                                                      required: true,
                                                      message: `Please enter ${field.label.replace(':', '')}`,
                                                  },
                                              ]
                                            : undefined
                                    }
                                >
                                    {renderPromoField(
                                        field,
                                        options,
                                        disabled,
                                        promoFormValues,
                                        promoForm,
                                        planYear,
                                        handlePromoFormValuesChange,
                                        state.dateConstraints,
                                        unitOptions,
                                        tpoFieldTestId('promo', field.name),
                                        state.required,
                                    )}
                                </Form.Item>
                            );
                        })}
                        {Array.from({ length: ITEMS_PER_ROW - row.length }).map((_, i) => (
                            <div key={`spacer-${i}`} className="flex-1" />
                        ))}
                    </div>
                </div>
            ))}
        </Form>
    );
};

PromoMetaForm.displayName = 'PromoMetaForm';

export default PromoMetaForm;
